/** KAN-718: тестовое письмо с сохранённого ящика на указанный адрес. */
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEmailMailboxMutations } from '@/hooks/useEmailMailbox';
import { extractErrorMessage } from '@/lib/extractErrorMessage';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTo: string;
}

export function TestSendDialog({ open, onOpenChange, defaultTo }: Props): JSX.Element {
  const { testSend } = useEmailMailboxMutations();
  const [to, setTo] = useState(defaultTo);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (next: boolean): void => {
    if (next) {
      setTo(defaultTo);
      setError(null);
    }
    onOpenChange(next);
  };

  const send = (): void => {
    setError(null);
    testSend.mutate(to.trim(), {
      onSuccess: (result) => {
        if (result.ok) {
          toast.success(`Тестовое письмо отправлено на ${to.trim()}`);
          onOpenChange(false);
        } else {
          setError(result.error || 'Письмо не отправлено');
        }
      },
      onError: (err) => setError(extractErrorMessage(err, 'Письмо не отправлено')),
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="mail-test-send-dialog">
        <DialogHeader>
          <DialogTitle>Тестовое письмо</DialogTitle>
          <DialogDescription>Письмо уйдёт с сохранённого ящика.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="mail-test-to" className="text-xs">
            Кому
          </Label>
          <Input
            id="mail-test-to"
            type="email"
            value={to}
            placeholder="name@example.ru"
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        {error && (
          <p className="text-xs text-red-700" data-testid="mail-test-send-error">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            data-testid="mail-test-send-submit"
            disabled={!to.trim() || testSend.isPending}
            onClick={send}
          >
            {testSend.isPending ? 'Отправка...' : 'Отправить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
